<template>
  <div>
    <div class="flex items-center justify-between mb-5">
      <span class="font-display font-bold text-lg text-profissa-text-main">Vue d'ensemble — Antifraude</span>
      <el-button :loading="isLoading" @click="$emit('refresh')" :icon="Refresh">
        Actualiser
      </el-button>
    </div>

    <el-empty v-if="!hasData" description="Aucune donnée d'analyse disponible" />

    <div v-else class="grid gap-6">

      <!-- Alertes par type -->
      <el-card v-if="alertsByType.length" shadow="never">
        <template #header>
          <span class="font-bold text-sm inline-flex items-center gap-2">
            <i class="mdi mdi-alert-outline" aria-hidden="true"></i>
            Alertes par type
          </span>
        </template>
        <div class="flex flex-col gap-3">
          <div v-for="item in alertsByType" :key="item.type" class="flex items-center gap-3">
            <div class="w-40 text-xs font-bold text-profissa-text-muted shrink-0">
              {{ translateAlertType(item.type) }}
            </div>
            <el-progress
              :percentage="barPct(item.count, maxAlertCount)"
              :color="alertTypeColor(item.type)"
              :show-text="false"
              class="flex-1"
            />
            <div class="w-8 text-right font-bold text-sm">{{ item.count }}</div>
          </div>
        </div>
      </el-card>

      <!-- Alertes par statut -->
      <el-card v-if="alertsByStatus.length" shadow="never">
        <template #header>
          <span class="font-bold text-sm inline-flex items-center gap-2">
            <i class="mdi mdi-format-list-bulleted" aria-hidden="true"></i>
            Alertes par statut
          </span>
        </template>
        <div class="flex flex-wrap gap-3">
          <el-card
            v-for="item in alertsByStatus"
            :key="item.status"
            shadow="never"
            class="flex-1 min-w-[120px] text-center"
          >
            <el-statistic :value="item.count">
              <template #title>
                <el-tag :type="statusTagType(item.status)" size="small" class="uppercase font-bold tracking-wider">{{ translateStatus(item.status) }}</el-tag>
              </template>
            </el-statistic>
          </el-card>
        </div>
      </el-card>

      <!-- Sanctions -->
      <el-card v-if="sanctionCounts.length" shadow="never">
        <template #header>
          <span class="font-bold text-sm inline-flex items-center gap-2">
            <i class="mdi mdi-scale-balance" aria-hidden="true"></i>
            État des sanctions artisans
          </span>
        </template>
        <div class="flex flex-wrap gap-3">
          <el-card
            v-for="item in sanctionCounts"
            :key="item.state"
            shadow="never"
            class="flex-1 min-w-[120px] text-center"
          >
            <el-statistic :value="item.count">
              <template #title>
                <el-tag :type="sanctionTagType(item.state)" size="small" class="uppercase font-bold tracking-wider">{{ translateSanction(item.state) }}</el-tag>
              </template>
            </el-statistic>
          </el-card>
        </div>
      </el-card>

      <!-- Artisans à risque -->
      <el-card v-if="topRisky.length" shadow="never">
        <template #header>
          <span class="font-bold text-sm inline-flex items-center gap-2">
            <i class="mdi mdi-alert-circle-outline" aria-hidden="true"></i>
            Artisans à risque élevé
          </span>
        </template>
        <div class="flex flex-col gap-2">
          <div
            v-for="(artisan, idx) in topRisky"
            :key="artisan.id"
            class="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-profissa-glass-border"
          >
            <div class="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center font-bold text-xs text-red-500 shrink-0">
              {{ idx + 1 }}
            </div>
            <div class="flex-1">
              <div class="font-bold text-sm">{{ artisan.full_name || artisan.phone || 'Artisan' }}</div>
              <div class="text-xs text-profissa-text-muted">{{ artisan.quartier || '' }}</div>
            </div>
            <div class="flex items-center gap-1">
              <span class="font-black text-base text-red-500">{{ artisan.risk_score?.toFixed(1) }}</span>
              <span class="text-[0.7rem] text-profissa-text-muted">/ risque</span>
            </div>
            <el-tag :type="sanctionTagType(artisan.sanction_state)" size="small" class="uppercase font-bold tracking-wider">
              {{ translateSanction(artisan.sanction_state) }}
            </el-tag>
          </div>
        </div>
      </el-card>

      <!-- Taux d'annulation client élevé (fausses annulations potentielles) -->
      <el-card v-if="highCancelRateArtisans.length" shadow="never">
        <template #header>
          <div class="flex items-center justify-between">
            <span class="font-bold text-sm inline-flex items-center gap-2">
              <i class="mdi mdi-chart-line-variant" aria-hidden="true"></i>
              Taux d'annulation client suspect
            </span>
            <el-tooltip content="Artisans avec ≥5 missions dont le taux d'annulation par le client est anormalement élevé — signal potentiel d'accord hors-plateforme.">
              <el-icon class="text-profissa-text-muted cursor-help"><InfoFilled /></el-icon>
            </el-tooltip>
          </div>
        </template>
        <div class="flex flex-col gap-2">
          <div
            v-for="(artisan, idx) in highCancelRateArtisans"
            :key="artisan.artisan_id"
            class="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-profissa-glass-border"
          >
            <div class="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center font-bold text-xs text-orange-500 shrink-0">
              {{ idx + 1 }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-bold text-sm truncate">{{ artisan.full_name || artisan.phone || 'Artisan' }}</div>
              <div class="text-xs text-profissa-text-muted">
                {{ artisan.client_cancellations }} annulations / {{ artisan.total_missions }} missions
              </div>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <el-progress
                type="circle"
                :percentage="Math.round(artisan.cancel_rate * 100)"
                :width="44"
                :stroke-width="4"
                :color="cancelRateColor(artisan.cancel_rate)"
              >
                <template #default="{ percentage }">
                  <span class="text-[0.65rem] font-black" :style="{ color: cancelRateColor(artisan.cancel_rate) }">
                    {{ percentage }}%
                  </span>
                </template>
              </el-progress>
            </div>
          </div>
        </div>
      </el-card>

    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { Refresh, InfoFilled } from '@element-plus/icons-vue'

const props = defineProps({
  analytics: { type: Object, default: () => ({}) },
  isLoading:  { type: Boolean, default: false },
})
defineEmits(['refresh'])

const ALERT_LABELS = {
  fake_cancellation:    'Fausse annulation',
  proximity_no_mission: 'Proximité suspecte',
  arrival_out_of_zone:  'Arrivée hors zone',
  photo_anomaly:        'Anomalie photos',
  price_anomaly:        'Anomalie tarif',
}
const STATUS_LABELS = {
  open:         'Ouvert',
  investigating:'En examen',
  confirmed:    'Confirmé',
  dismissed:    'Non retenu',
}
const SANCTION_LABELS = {
  active:       'Actif',
  under_review: 'Sous surveillance',
  suspended:    'Suspendu',
}

const hasData                = computed(() => props.analytics && Object.keys(props.analytics).length > 0)
const alertsByType           = computed(() => props.analytics?.alerts_by_type          || [])
const alertsByStatus         = computed(() => props.analytics?.alerts_by_status        || [])
const sanctionCounts         = computed(() => props.analytics?.sanctions               || [])
const topRisky               = computed(() => props.analytics?.top_risky_artisans      || [])
const highCancelRateArtisans = computed(() => props.analytics?.high_cancel_rate_artisans || [])
const maxAlertCount          = computed(() => Math.max(...alertsByType.value.map(i => i.count), 1))

const barPct = (count, max) => Math.round((count / max) * 100)

const translateAlertType = (t) => ALERT_LABELS[t] || t
const translateStatus    = (s) => STATUS_LABELS[s] || s
const translateSanction  = (s) => SANCTION_LABELS[s] || s

const alertTypeColor = (t) => ({
  fake_cancellation: '#DC2626', proximity_no_mission: '#D97706',
  arrival_out_of_zone: '#0EA5E9',
  photo_anomaly: '#059669', price_anomaly: '#F59E0B',
}[t] || '#9CA3AF')

const statusTagType = (s) => ({
  open: 'warning', investigating: 'primary', confirmed: 'danger', dismissed: 'info'
}[s] || 'info')

const sanctionTagType = (s) => ({
  active: 'success', under_review: 'warning', suspended: 'danger'
}[s] || 'info')

const cancelRateColor = (rate) => {
  if (rate >= 0.6) return '#DC2626'   // red — very high
  if (rate >= 0.4) return '#D97706'   // amber — high
  return '#F59E0B'                     // yellow — elevated
}
</script>
